/**
 * Checks whether a value is a non-empty string.
 *
 * Empty or whitespace-only strings are treated as invalid.
 *
 * @param value The value to evaluate.
 * @returns `true` when the value is a string with at least one non-whitespace character.
 */
export function isString(value: unknown): value is string {
  if (value === null) return false;
  return typeof value === 'string' && value.trim() !== '';
}

/**
 * Checks whether a value can be interpreted as a number.
 *
 * Accepts finite numbers and numeric strings (for example route params like `"123"`).
 * Rejects `null`, `undefined`, empty strings, and non-numeric values.
 *
 * @param value The value to evaluate.
 * @returns `true` when the value is numeric or can be safely coerced into a number.
 */
export function isNumber(value: unknown): value is number {
  if (!isDefined(value)) return false;
  // valid number or a string value that can be converted to a number
  // common use cases number id extracted as string from router params
  if (value === '') return false;
  return (
    (typeof value === 'string' || typeof value === 'number') && !isNaN(Number(value))
  );
}

/**
 * Checks whether a value is a finite, safe integer number.
 *
 * This is stricter than {@link isNumber}: decimals and values outside
 * JavaScript's safe integer range are rejected.
 *
 * @param value The value to evaluate.
 * @returns `true` when the value is a finite safe integer.
 */
export function isSafeNumber(value: unknown): value is number {
  return (
    typeof value === 'number' && Number.isFinite(value) && Number.isSafeInteger(value)
  );
}

/**
 * Checks whether a value can be interpreted as a boolean.
 *
 * Accepts boolean values directly, numeric booleans (`0` and `1`),
 * and string booleans (`"true"` and `"false"`, case-insensitive).
 *
 * @param value The value to evaluate.
 * @returns `true` when the value represents a boolean.
 */
export function isBoolean(value: unknown): value is boolean {
  if (typeof value === 'boolean') return true;
  if (typeof value === 'number') return value === 1 || value === 0;
  if (typeof value === 'string')
    return value.toLowerCase() === 'true' || value.toLowerCase() === 'false';
  return false;
}

/**
 * Checks whether a value is neither `null` nor `undefined`.
 *
 * Useful as a guard before accessing properties on optional values.
 *
 * @param value The value to evaluate.
 * @returns `true` when the value is defined.
 */
export function isDefined<T>(value: T): value is NonNullable<T> {
  return value !== null && value !== undefined;
}

type AnyFunction = (...args: unknown[]) => unknown;

/**
 * Checks whether a value is a function.
 *
 * @param value The value to evaluate.
 * @returns `true` when the value is callable.
 */
export function isFunction<T extends AnyFunction = AnyFunction>(
  value: unknown,
): value is T {
  if (value === null) return false;
  return typeof value === 'function';
}
